import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLoading } from '../../store/actions';
import MetaTags from "react-meta-tags";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  Label,
  Table,
  Button,
  ModalFooter,
  ModalBody,
  ModalHeader,
} from "reactstrap";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "../api/axios";
import { debounce } from "lodash";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";

const VendorSpecialty = () => {
  const dispatch = useDispatch();

  const [searchVendor, setSearchVendor] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [specialtyModal, setSpecialtyModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [newSpecialty, setNewSpecialty] = useState("");
  const [specialtyError, setSpecialtyError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const decode = jwt_decode(window.localStorage.getItem("accessToken") || "");

  const ADD_SPECIALTY = "/vendorspeciality/add_speciality";
  const EDIT_SPECIALTY = "/vendorspeciality/edit_speciality";
  const DELETE_SPECIALTY = "/vendorspeciality/delete_speciality";
  const GET_SPECIALTY = `/vendorspeciality/get_speciality?searchVendor=${searchVendor}&companyId=${decode?.id || ""}`;

  const debouncedSearch = useMemo(
    () => debounce((value) => setSearchVendor(value.trim()), 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const getSpecialities = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const res = await axios.get(GET_SPECIALTY);

      if (res.data?.success) {
        setSpecialties(res.data.specialties ?? []);
      } else {
        toast.error(res.data?.errorMessage || "Failed to load categories");
      }
    } catch (err) {
      console.error("Fetch specialties error:", err);
      const msg = err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        "Failed to load vendor categories";
      toast.error(msg);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, GET_SPECIALTY]);

  useEffect(() => {
    getSpecialities();
  }, [searchVendor, getSpecialities]);

  const handleAddOrEdit = async (e) => {
    e.preventDefault();

    const trimmed = newSpecialty.trim();
    if (!trimmed) {
      setSpecialtyError("Please enter a category name");
      return;
    }

    setSpecialtyError("");
    setSubmitting(true);
    dispatch(setLoading(true));

    try {
      const payload = {
        specialty: trimmed,                    // ← FIXED: correct field name
        ...(isEdit ? { id: selectedId } : { createdBy: decode?.id || "" }),
      };

      const url = isEdit ? EDIT_SPECIALTY : ADD_SPECIALTY;
      const res = await axios.post(url, payload);

      if (res.data?.success) {
        toast.success(isEdit ? "Category updated successfully" : "Category added successfully");
        setSpecialtyModal(false);
        setNewSpecialty("");
        setIsEdit(false);
        setSelectedId("");
        await getSpecialities();
      } else {
        setSpecialtyError(res.data?.errorMessage || "Operation failed");
        toast.warning(res.data?.errorMessage || "Operation failed");
      }
    } catch (err) {
      console.error("Save specialty error:", err);
      const msg =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        err.message ||
        "Something went wrong. Please try again.";
      setSpecialtyError(msg);
      toast.error(msg);
    } finally {
      dispatch(setLoading(false));
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;

    dispatch(setLoading(true));

    try {
      const res = await axios.delete(`${DELETE_SPECIALTY}?id=${selectedId}`);
      if (res.data?.success) {
        toast.success(res.data.message || "Category deleted successfully");
        await getSpecialities();
      } else {
        toast.warning(res.data.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      const msg = err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        "Failed to delete category";
      toast.error(msg);
    } finally {
      dispatch(setLoading(false));
      setShowDeleteModal(false);
      setSelectedId("");
    }
  };

  const openEdit = (specialty) => {
    setIsEdit(true);
    setSelectedId(specialty._id);
    setNewSpecialty(specialty.specialty || "");
    setSpecialtyError("");
    setSpecialtyModal(true);
  };

  const openAdd = () => {
    setIsEdit(false);
    setSelectedId("");
    setNewSpecialty("");
    setSpecialtyError("");
    setSpecialtyModal(true);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | Vendor Categories</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Vendor" breadcrumbItem="Vendor Categories" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row className="mb-3 align-items-center">
                    <Col sm="6">
                      {/* Uncomment to enable search */}
                      {/* <div className="search-box">
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search category..."
                            onChange={(e) => debouncedSearch(e.target.value)}
                          />
                          <i className="bx bx-search-alt search-icon" />
                        </div>
                      </div> */}
                    </Col>

                    <Col sm="6" className="text-sm-end">
                      <button
                        className="btn btn-primary"
                        onClick={openAdd}
                        disabled={submitting}
                      >
                        <i className="bx bx-plus me-1" />
                        Add Category
                      </button>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <Table className="table table-centered table-nowrap mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>#</th>
                          <th>Category Name</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {specialties.map((item, index) => (
                          <tr key={item._id}>
                            <td>{index + 1}</td>
                            <td>{item.specialty}</td>
                            <td>
                              <span
                                className={`badge bg-${item.active ? "success" : "danger"
                                  }-soft`}
                              >
                                {item.active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-3">
                                <Link to="#" className="text-success">
                                  <i
                                    className="mdi mdi-pencil font-size-18"
                                    onClick={() => openEdit(item)}
                                  />
                                </Link>
                                <Link to="#" className="text-danger">
                                  <i
                                    className="mdi mdi-delete font-size-18"
                                    onClick={() => {
                                      setSelectedId(item._id);
                                      setShowDeleteModal(true);
                                    }}
                                  />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {specialties.length === 0 && !dispatch.loading && (
                          <tr>
                            <td colSpan={4} className="text-center py-5 text-muted">
                              No categories found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        isOpen={specialtyModal}
        toggle={() => !submitting && setSpecialtyModal(false)}
        centered
        backdrop={submitting ? "static" : true}
      >
        <ModalHeader toggle={() => !submitting && setSpecialtyModal(false)}>
          {isEdit ? "Edit Category" : "Add New Category"}
        </ModalHeader>
        <ModalBody>
          {specialtyError && (
            <div className="alert alert-danger">{specialtyError}</div>
          )}

          <div className="mb-3">
            <Label htmlFor="specialty-name">Category Name</Label>
            <input
              id="specialty-name"
              className="form-control"
              placeholder="Enter category name"
              value={newSpecialty}
              onChange={(e) => {
                setNewSpecialty(e.target.value);
                setSpecialtyError("");
              }}
              onBlur={(e) => setNewSpecialty(e.target.value.trim())}
              disabled={submitting}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => setSpecialtyModal(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleAddOrEdit}
            disabled={submitting || !newSpecialty.trim()}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {isEdit ? "Updating..." : "Adding..."}
              </>
            ) : isEdit ? (
              "Update"
            ) : (
              "Add"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(false)}
        centered
      >
        <ModalHeader toggle={() => setShowDeleteModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this category?<br />
          <small className="text-muted">This action cannot be undone.</small>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default VendorSpecialty;